/**
 * @file useBoardRedeliver.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-15
 * @description "Refused — redeliver…" on the transport planning boards
 *              (TransportPlanning and HaulageJobsPlanning), for a row's
 *              right-click menu and its three-dots menu alike — one row at a
 *              time, like "Archive order…" (hooks/useBoardArchive). On a
 *              redelivery's own row it is "Edit redelivery…".
 *
 *              Nothing about redelivery is decided here: the window is
 *              RedeliverOrderModal, the rules utils/redelivery, the server
 *              services/orderRedeliveryService. The board only knows what its
 *              row knows (an invoiced or archived order is not on a board), so
 *              it greys what it can — a haulage job, a self-billed PO, a
 *              Customer Collection / Third-Party / Direct Delivery order, a
 *              delivery still to come — and the window says the rest.
 *
 *              Usage:
 *                const redeliver = useBoardRedeliver({ user, selectedCount, onDone, showAlert });
 *                rowActions: [...redeliver.actionsFor(row)]
 *                render: {redeliver.element}
 */

import React, { useState } from 'react';
import RedeliverOrderModal from '../components/RedeliverOrderModal';
import { canRedeliver } from '../utils/redeliveryActions';
import { isReadOnly } from '../utils/permissions';
import { redeliverBlock } from '../utils/redelivery';
import { isOptionCandidate } from '../utils/planningDay';

/** Why this board row's order cannot be redelivered from here, or null. */
export function boardRedeliverBlock(row, user) {
  if (!row) return 'No row';
  if (row.is_haulage_job) return 'A haulage job, not a customer order';
  if (row.is_self_billed) return 'A self-billed PO has no customer order';
  if (!row.id) return 'There is no customer order behind this row';
  if (isReadOnly(user)) return 'View-only accounts cannot record a refused delivery';
  if (!canRedeliver(user)) return 'Needs Edit Transport Planning or Edit sales orders';
  if (row.is_redelivery) return null;
  if (isOptionCandidate(row)) return `${row.order_number || 'This order'} is planned on another of its option dates`;
  // The board knows the order's day and terms; the window checks the rest.
  return redeliverBlock({
    order_number: row.order_number,
    delivery_date: row.delivery_date,
    supplier_delivery_terms: row.supplier_delivery_terms,
    excel_agri_delivery_terms: row.excel_agri_delivery_terms,
    has_supply_pos: false
  });
}

export default function useBoardRedeliver({ user, selectedCount = 0, onDone, showAlert }) {
  const [orderId, setOrderId] = useState(null);

  const actionsFor = (row) => {
    const block = boardRedeliverBlock(row, user);
    const edit = !!row?.is_redelivery;
    return [{
      key: 'redeliver',
      icon: edit ? 'edit_calendar' : 'assignment_return',
      label: edit ? 'Edit redelivery…' : 'Refused — redeliver…',
      disabled: !!block,
      title: block || (edit
        ? `Change ${row.order_number || 'the order'}'s redelivery — its date, where it is held, or cancel it`
        : `The customer refused ${row.order_number || 'it'} — record where it is held and when it goes again`),
      onClick: () => {
        if (block || orderId) return;
        if (selectedCount > 1) {
          showAlert({
            title: 'One row at a time',
            type: 'warning',
            message: `${selectedCount} rows are selected. Record a refused delivery one order at a time — right-click a single row.`
          });
          return;
        }
        setOrderId(row.id);
      }
    }];
  };

  const element = orderId ? (
    <RedeliverOrderModal
      orderId={orderId}
      onClose={() => { setOrderId(null); onDone && onDone(); }}
      onDone={(message) => message && showAlert({ title: 'Redelivery', type: 'success', message })}
    />
  ) : null;

  return { actionsFor, element };
}
